import { Component, Output, EventEmitter } from '@angular/core';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { CredentialType } from 'enums/CredentialType';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CredentialRequired } from 'services/credential.service';
import { SelectListItemModel } from 'models/SelectList';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { SubscriptionDetailsModel } from 'models/generated/Distribution.Api.Models.Csp.SubscriptionDetails';
import { DateService } from 'services/date.service';
import { Pipes } from 'modules/pipes';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { NavigationService } from 'services/navigation.service';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { ProductPlanService } from 'services/productplan.service';
import { AccountingService } from 'services/accounting.service';
import { CspService } from 'services/csp.service';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { ListView } from 'modules/list-view';

@Component({
	selector: 'company-microsoft365-attach-to-plan',
	templateUrl: './company-microsoft365-attach-to-plan.component.html',
	imports: [Layout, ListView, CustomSelectComponent, Pipes]
})
@CredentialRequired(CredentialType.EditMicrosoft365)
export class CompanyMicrosoft365AttachToPlanComponent {

	@Output() public productPlanAttached = new EventEmitter<string>();

	availableProductPlans: SelectListItemModel[] = [];
	selectedProductPlan?: ProductPlanModel;
	selectedSubscription?: SubscriptionDetailsModel;
	newEndDate?: Date;
	newEndDateList: SelectListItemModel[] = [];
	ledgerPosts: LedgerPostModel[] = [];
	mismatchInEndDates = false;

	constructor(
		private translate: TranslatePipe,
		private customDate: CustomDatePipe,
		private readonly dateService: DateService,
		private navigation: NavigationService,
		private readonly productPlanService: ProductPlanService,
		private readonly accountingService: AccountingService,
		private readonly cspService: CspService
	) { }

	get newProductPlanName() {
		return this.selectedSubscription ?
			this.selectedSubscription.productName + (this.selectedSubscription.quantity > 1 ? ` - ${this.selectedSubscription.quantity} ${this.translate.transform('general.pieces')}` : '') : ''
	}

	get subscriptionEndDate() { return this.selectedSubscription && this.selectedSubscription.endDate ? this.dateService.getDate(this.selectedSubscription?.endDate) : undefined; }

	productPlanSelected() {
		this.newEndDate = undefined;
		let newEndDateList = [];
		if (this.selectedProductPlan && this.selectedSubscription) {

			this.mismatchInEndDates = this.selectedProductPlan.autoRenewalDate?.getDate() != this.subscriptionEndDate?.getDate() || this.selectedProductPlan.autoRenewalDate?.getMonth() != this.subscriptionEndDate?.getMonth();

			const subscriptionEndDate = this.subscriptionEndDate;
			const originalEndDate = this.selectedProductPlan.autoRenewalDate;
			const today = this.dateService.today();
			const ledgerPostsForSelectedPlan = this.ledgerPosts?.filter(lp => lp.accountingPlan?.productPlanId == this.selectedProductPlan?.id || lp.productPlanId == this.selectedProductPlan?.id);
			const latestLedgerPostPlannedDate = ledgerPostsForSelectedPlan?.length ? ledgerPostsForSelectedPlan.filter(post => post.plannedDate).reduce((latest, current) => current.plannedDate! > latest.plannedDate! ? current : latest).plannedDate : undefined;

			if (!subscriptionEndDate || !originalEndDate || subscriptionEndDate == originalEndDate) return;

			let nearestEndDateBeforeOriginalEndDate = new Date(originalEndDate.getFullYear(), subscriptionEndDate.getMonth(), subscriptionEndDate.getDate());
			if (nearestEndDateBeforeOriginalEndDate > originalEndDate) {
				while (nearestEndDateBeforeOriginalEndDate > originalEndDate) {
					nearestEndDateBeforeOriginalEndDate = this.dateService.addYears(nearestEndDateBeforeOriginalEndDate, -1);
				}
			}

			if (nearestEndDateBeforeOriginalEndDate < originalEndDate && nearestEndDateBeforeOriginalEndDate > today && (latestLedgerPostPlannedDate == undefined || latestLedgerPostPlannedDate < nearestEndDateBeforeOriginalEndDate)) {
				newEndDateList.push(new SelectListItemModel(this.dateService.getDate(nearestEndDateBeforeOriginalEndDate), this.translate.transform('microsoft365.attach-to-plan.option.nearest-end-date-before-original-end-date', {
					originalEndDate: this.customDate.transform(originalEndDate, 'shortDate'),
					subscriptionEndDate: this.customDate.transform(subscriptionEndDate, 'shortDate'),
					nearestEndDateBeforeOriginalEndDate: this.customDate.transform(nearestEndDateBeforeOriginalEndDate, 'shortDate'),
					daysDifference: this.dateService.getDiffInDays(nearestEndDateBeforeOriginalEndDate, originalEndDate)
				})));
			}

			let nearestEndDateAfterOriginalEndDate = new Date(originalEndDate.getFullYear(), subscriptionEndDate.getMonth(), subscriptionEndDate.getDate());
			if (nearestEndDateAfterOriginalEndDate < originalEndDate) {
				while (nearestEndDateAfterOriginalEndDate < originalEndDate) {
					nearestEndDateAfterOriginalEndDate = this.dateService.addYears(nearestEndDateAfterOriginalEndDate, 1);
				}
			}

			newEndDateList.push(new SelectListItemModel(this.dateService.getDate(nearestEndDateAfterOriginalEndDate), this.translate.transform('microsoft365.attach-to-plan.option.nearest-end-date-after-original-end-date', {
				originalEndDate: this.customDate.transform(originalEndDate, 'shortDate'),
				subscriptionEndDate: this.customDate.transform(subscriptionEndDate, 'shortDate'),
				nearestEndDateAfterOriginalEndDate: this.customDate.transform(nearestEndDateAfterOriginalEndDate, 'shortDate'),
				daysDifference: this.dateService.getDiffInDays(nearestEndDateAfterOriginalEndDate, originalEndDate)
			})));

		}
		this.newEndDateList = newEndDateList;
	}

	canSave() {
		return !!this.selectedProductPlan &&
			!!this.selectedSubscription &&
			((this.mismatchInEndDates && !!this.newEndDate) || !this.mismatchInEndDates);
	}

	async saveAndAttachPlan() {
		if (!this.canSave()) return;

		let tags = this.selectedProductPlan?.productPlanTags ? [...this.selectedProductPlan.productPlanTags.map(t => t.tag)] : [];
		tags.push(ProductPlanTagConstants.microsoft365);

		const accountingPlan = this.accountingService.filterCurrentAccountingPlan(this.selectedProductPlan!.accountingPlans);
		const expectedTotalPlanValue = this.ledgerPosts.filter(post => post.accountingPlanId == accountingPlan.id).map(post => post.signedValue).reduce((partialSum, a) => partialSum + a, 0);

		const endDate = this.mismatchInEndDates ? this.newEndDate : this.selectedProductPlan!.autoRenewalDate;
		if (!endDate) return;

		let productPlanPromises: Promise<void>[] = [];
		productPlanPromises.push(this.productPlanService.updateProductPlanName(this.selectedProductPlan!.id!, this.newProductPlanName, true));
		productPlanPromises.push(this.productPlanService.setProductPlanRestrictEndDate(this.selectedProductPlan!.id!, endDate, true));
		productPlanPromises.push(this.productPlanService.setProductPlanTags(this.selectedProductPlan!.id!, tags));
		await Promise.all(productPlanPromises);
		if (this.mismatchInEndDates) await this.accountingService.changeAccountingPlanEndDate(accountingPlan.id!, endDate, expectedTotalPlanValue);
		await this.cspService.assignSubscriptionToExistingProductPlan(this.selectedSubscription!.id, this.selectedProductPlan!.id!, endDate, this.selectedProductPlan!.terminationDate != undefined, this.selectedSubscription!.autoRenewEnabled!);
		
		this.productPlanAttached.emit(this.selectedProductPlan!.id!.toString());
	}

	cancel() {
		this.navigation.closeView(this);
	}
	
}
